import { useEffect, useRef, useState } from 'react';
import { Progress, Stack, Text } from '@chakra-ui/react';
import { CheckCircle2, Download, X } from 'lucide-react';
import { ConfirmSheet } from '../ConfirmSheet';
import {
  apiExportSource,
  canStreamToFile,
  chooseSaveTarget,
  exportEntries,
  exportFileName,
  formatSize,
  planExport,
  splitPlan,
  writeZip,
  type ExportPlan,
  type ExportProgress,
  type ExportSource,
  type SaveTarget,
} from '../../lib/familyExport';
import { useT } from '../../i18n';

interface FamilyExportSheetProps {
  isOpen: boolean;
  onClose: () => void;
  familyId: string;
  familyName: string;
  currentUserId?: string;
  /** Tests pass a fake; the app reads its own endpoints (D193). */
  source?: ExportSource;
}

/** One download: its parts, and how far it has got. */
interface Run {
  plan: ExportPlan;
  parts: ExportPlan[];
  names: string[];
  /** Files that went into the parts saved so far. */
  written: number;
  /** Parts saved so far, in order. */
  saved: number;
}

type State =
  | { step: 'planning'; progress: ExportProgress | null }
  | { step: 'ready'; plan: ExportPlan }
  | { step: 'running'; run: Run; progress: ExportProgress | null }
  | { step: 'done'; run: Run }
  | { step: 'failed'; plan: ExportPlan | null };

// Download photos and documents (M54, D192): built on this device. The
// sheet first looks through the chats so it can say how big it will be;
// where the zip has to be held in memory (phones, Safari) it comes in parts
// of up to 500 MB. "X of Y files" and Cancel while it runs.
export function FamilyExportSheet({
  isOpen,
  onClose,
  familyId,
  familyName,
  currentUserId,
  source,
}: FamilyExportSheetProps) {
  const t = useT();
  const [state, setState] = useState<State>({
    step: 'planning',
    progress: null,
  });
  const abort = useRef<AbortController | null>(null);
  const from = source ?? apiExportSource(familyId, currentUserId);
  const fromRef = useRef(from);
  fromRef.current = from;
  const inMemory = !canStreamToFile();

  // Look through everything as soon as the sheet opens (D192).
  const planning = state.step === 'planning';
  useEffect(() => {
    if (!isOpen || !planning) return;
    const controller = new AbortController();
    abort.current = controller;
    planExport(fromRef.current, {
      signal: controller.signal,
      onProgress: (progress) =>
        setState((current) =>
          current.step === 'planning' ? { step: 'planning', progress } : current
        ),
    })
      .then((plan) => setState({ step: 'ready', plan }))
      .catch(() => {
        if (!controller.signal.aborted)
          setState({ step: 'failed', plan: null });
      });
    return () => controller.abort();
  }, [isOpen, planning]);

  const close = () => {
    // Closing by accident mustn't lose a long download: Cancel is explicit.
    if (state.step === 'running') return;
    abort.current?.abort();
    setState({ step: 'planning', progress: null });
    onClose();
  };

  /** Builds and saves one part; the next part waits for its own tap. */
  const runPart = async (run: Run, target: SaveTarget) => {
    const index = run.saved;
    const part = run.parts[index]!;
    const controller = new AbortController();
    abort.current = controller;
    const { signal } = controller;
    setState({ step: 'running', run, progress: null });
    const total = run.plan.files.length;
    const base = run.parts
      .slice(0, index)
      .reduce((sum, earlier) => sum + earlier.files.length, 0);
    const planned = new Set(part.files.map((file) => file.path));
    let written = 0;
    try {
      const entries = exportEntries(part, fromRef.current, {
        signal,
        onProgress: (progress) =>
          setState((current) =>
            current.step === 'running'
              ? {
                  ...current,
                  progress: {
                    phase: 'files',
                    done: base + progress.done,
                    total,
                  },
                }
              : current
          ),
      });
      // Count the files that actually went in, for the summary.
      const counted = (async function* () {
        for await (const entry of entries) {
          if (planned.has(entry.name)) written += 1;
          yield entry;
        }
      })();
      await writeZip(counted, target, signal);
      setState({
        step: 'done',
        run: { ...run, written: run.written + written, saved: index + 1 },
      });
    } catch {
      if (signal.aborted) {
        setState(
          run.saved > 0
            ? { step: 'done', run }
            : { step: 'ready', plan: run.plan }
        );
      } else {
        setState({ step: 'failed', plan: run.plan });
      }
      if (target.kind === 'file') {
        await target.writable.abort().catch(() => undefined);
      }
    } finally {
      abort.current = null;
    }
  };

  const start = async (plan: ExportPlan) => {
    const now = new Date();
    // In memory (phones, Safari): parts of up to 500 MB, each saved by its
    // own tap, since Safari blocks downloads that follow one another.
    const parts = inMemory ? splitPlan(plan) : [plan];
    const names = parts.map((_, index) =>
      exportFileName(familyName, now, parts.length > 1 ? index + 1 : undefined)
    );
    // First, while the tap still counts: where to save, where it's asked.
    const target = await chooseSaveTarget(names[0]!);
    if (!target) return;
    await runPart({ plan, parts, names, written: 0, saved: 0 }, target);
  };

  const saveNextPart = (run: Run) =>
    void runPart(run, { kind: 'blob', fileName: run.names[run.saved]! });

  if (state.step === 'running') {
    const progress = state.progress;
    const percent =
      progress && progress.total > 0
        ? Math.round((progress.done / progress.total) * 100)
        : 0;
    return (
      <ConfirmSheet
        isOpen={isOpen}
        onClose={close}
        centered
        danger={false}
        icon={<Download size={28} />}
        title={t('account.exportFamily.title')}
        body={
          <Stack spacing={3} textAlign="left" aria-live="polite">
            <Text fontWeight={700} color="ink">
              {t('account.exportFamily.progress', {
                done: progress?.done ?? 0,
                total: progress?.total ?? state.run.plan.files.length,
              })}
            </Text>
            <Progress
              value={percent}
              size="sm"
              borderRadius="full"
              colorScheme="brand"
              aria-label={t('account.exportFamily.title')}
            />
            <Text fontSize="15px">{t('account.exportFamily.phoneNote')}</Text>
          </Stack>
        }
        confirmLabel={t('account.exportFamily.cancel')}
        confirmIcon={<X size={18} />}
        onConfirm={() => abort.current?.abort()}
        hideCancel
      />
    );
  }

  if (state.step === 'done') {
    const { run } = state;
    const count = run.parts.length;
    const more = run.saved < count;
    const inSaved = run.parts
      .slice(0, run.saved)
      .reduce((sum, part) => sum + part.files.length, 0);
    const skipped = run.plan.notIncluded.length + (inSaved - run.written);
    return (
      <ConfirmSheet
        isOpen={isOpen}
        onClose={close}
        centered
        danger={false}
        icon={<CheckCircle2 size={28} />}
        title={
          more
            ? t('account.exportFamily.partSaved', {
                part: run.saved,
                count,
              })
            : t('account.exportFamily.doneTitle')
        }
        body={
          <Stack spacing={2}>
            <Text>
              {more
                ? t('account.exportFamily.nextPart')
                : count > 1
                  ? t('account.exportFamily.doneParts', {
                      count: run.written,
                      parts: count,
                    })
                  : t('account.exportFamily.doneBody', {
                      count: run.written,
                      file: run.names[0]!,
                    })}
            </Text>
            {!more && skipped > 0 && (
              <Text>
                {t('account.exportFamily.skipped', { count: skipped })}
              </Text>
            )}
          </Stack>
        }
        confirmLabel={
          more
            ? t('account.exportFamily.savePart', {
                part: run.saved + 1,
                count,
              })
            : t('common.done')
        }
        confirmIcon={more ? <Download size={18} /> : undefined}
        onConfirm={() => (more ? saveNextPart(run) : close())}
        cancelLabel={t('common.close')}
        hideCancel={!more}
      />
    );
  }

  const plan = state.step === 'ready' ? state.plan : null;
  const parts = plan && inMemory ? splitPlan(plan).length : 1;
  return (
    <ConfirmSheet
      isOpen={isOpen}
      onClose={close}
      danger={false}
      icon={<Download size={28} />}
      title={t('account.exportFamily.title')}
      body={
        <Stack spacing={3} textAlign="left" aria-live="polite">
          {state.step === 'failed' && (
            <Text fontWeight={700} color="danger">
              {t('account.exportFamily.failed')}
            </Text>
          )}
          <Text>{t('account.exportFamily.body', { family: familyName })}</Text>
          <Text>{t('account.exportFamily.private')}</Text>
          {state.step === 'planning' && (
            <Stack spacing={2}>
              <Text fontWeight={700} color="ink">
                {t('account.exportFamily.scanning', {
                  done: state.progress?.done ?? 0,
                  total: state.progress?.total ?? 0,
                })}
              </Text>
              <Progress
                isIndeterminate
                size="sm"
                borderRadius="full"
                colorScheme="brand"
                aria-label={t('account.exportFamily.title')}
              />
            </Stack>
          )}
          {plan && (
            <Text fontWeight={700} color="ink">
              {t('account.exportFamily.size', {
                size: formatSize(plan.totalBytes),
                count: plan.files.length,
              })}
            </Text>
          )}
          {parts > 1 && (
            <Text>{t('account.exportFamily.parts', { count: parts })}</Text>
          )}
          <Text fontWeight={700}>{t('account.exportFamily.phoneNote')}</Text>
        </Stack>
      }
      confirmLabel={t('account.exportFamily.start')}
      confirmIcon={<Download size={18} />}
      confirmDisabled={state.step === 'planning'}
      cancelLabel={t('common.close')}
      onConfirm={() => {
        if (plan) void start(plan);
        else if (state.step === 'failed')
          setState({ step: 'planning', progress: null });
      }}
    />
  );
}
